import authAxios from "../authAxios";
import unauthAxios from "../axios";

export const getPublicHost = (userName) => {
  return unauthAxios.get(`/v1/affiliate/host/${encodeURIComponent(userName)}`);
};

export const updatePublicHost = (userName, hostPage) => {
  return authAxios.put(`/v1/affiliate/host/${encodeURIComponent(userName)}`, hostPage);
};

export const getAffiliateAnalytics = (timeframe) => {
  return authAxios.get("/v1/affiliate/analytics", {
    params: { timeframe },
  });
};

export const getAffiliateReferrals = ({ limit = 10, offset = 0 } = {}) => {
  return authAxios.get("/v1/affiliate/referrals", {
    params: {
      limit,
      offset,
    },
  });
};

// User-facing unified My Referrals view (works for all users, not just
// Popular Ducks). Returns lifetime totals broken down by source plus a
// paginated list of referrals.
export const getMyReferrals = ({ limit = 50, offset = 0 } = {}) => {
  return authAxios.get("/v1/affiliate/my-referrals", {
    params: { limit, offset },
  });
};

export const getAffiliateEarningsSummary = () => {
  return authAxios.get("/v1/affiliate/earnings-summary");
};

export const trackAffiliateView = (affiliateId) => {
  return unauthAxios.get("/v1/affiliate/track-view", {
    params: { affiliate_id: affiliateId },
  });
};

const getOrCreateClientId = () => {
  // Stable per-browser identifier. Lets the BE correlate a click row to a
  // later signup/booking from the same browser even when localStorage was
  // cleared between the click and the signup screen (cookie carries the
  // attribution, this id carries the analytics link).
  let id = localStorage.getItem("affiliate_client_id");
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("affiliate_client_id", id);
  }
  return id;
};

// Exposed for the club platform so a logged-out club-page visit can be claimed
// after login (POST /v1/club/claim-visits).
export const getAffiliateClientId = () => getOrCreateClientId();

export const trackAffiliateClick = async (affiliateId, { force = false } = {}) => {
  const trackedAffiliates = JSON.parse(localStorage.getItem("tracked_affiliate_clicks") || "[]");

  // Club pages pass `force`: every visit must be recorded because attribution
  // is a rolling 14-day window from the LATEST visit, not the first one.
  if (force || !trackedAffiliates.includes(affiliateId)) {
    try {
      const response = await unauthAxios.get("/v1/affiliate/track-click", {
        params: { affiliate_id: affiliateId, client_id: getOrCreateClientId() },
        withCredentials: true,
      });

      if (response.data.success && !trackedAffiliates.includes(affiliateId)) {
        trackedAffiliates.push(affiliateId);
        localStorage.setItem("tracked_affiliate_clicks", JSON.stringify(trackedAffiliates));
      }

      return response;
    } catch (error) {
      console.error("Error tracking affiliate click:", error);
      return { data: { success: false } };
    }
  }

  return { data: { success: true } };
};

export const createAffiliateReferral = (referralData) => {
  return authAxios.post("/v1/affiliate/referral", referralData);
};

export const requestWithdrawal = async (data) => {
  const { bankDetails } = data;
  const response = await authAxios.post("/v1/affiliate/withdrawals", {
    bankDetails,
  });
  return response;
};

export const getWithdrawals = async () => {
  const response = await authAxios.get("/v1/affiliate/withdrawals");
  return response;
};

export const getAffiliateBalances = async () => {
  const response = await authAxios.get("/v1/affiliate/balances");
  return response;
};

// Resolve a typed promo code at checkout. The backend decides whether it behaves
// as a duck-fam trip discount or a welcome/affiliate referral, based on the trip.
export const resolvePromoCode = (code, tripId) => {
  return authAxios
    .post("/v1/affiliate/resolve-promo-code", { code, trip_id: tripId })
    .then((res) => res.data);
};

// Fetch (minting on first call) the logged-in user's stable promo code to share.
export const getMyPromoCode = () => {
  return authAxios.get("/v1/affiliate/my-promo-code").then((res) => res.data);
};

export const getLuckyDrawReward = () => {
  return authAxios
    .get("/v1/affiliate/lucky-draw", { skipAuthRedirect: true })
    .then((res) => res.data);
};

export const getActiveLuckyDrawCampaign = () => {
  return unauthAxios.get("/v1/affiliate/lucky-draw/campaign").then((res) => res.data);
};

export const getLuckyDrawRewardForCampaign = (campaignId) => {
  return authAxios
    .get("/v1/affiliate/lucky-draw", {
      params: { campaign_id: campaignId },
      skipAuthRedirect: true,
    })
    .then((res) => res.data);
};

export const generateLuckyDrawReward = (campaignId) => {
  return authAxios
    .post("/v1/affiliate/lucky-draw", { campaign_id: campaignId })
    .then((res) => res.data);
};

// Rename the logged-in user's promo code to a custom handle. The backend rejects
// codes already taken by a campaign or another user. Always resolves to a
// `{ status, code? }` object (non-2xx responses carry the same shape) so callers
// can branch on status without a try/catch.
export const renameMyPromoCode = (code) => {
  return authAxios
    .post("/v1/affiliate/rename-promo-code", { code })
    .then((res) => res.data)
    .catch((err) => err.response?.data || { status: "error" });
};
